import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import api from '../services/api';
import BlogLayout from '../components/BlogLayout';
import SEO from '../components/SEO';
import NotFoundPage from './NotFoundPage';
import './BlogPage.css';
import { ArrowLeft, Calendar, User, Clock, BookOpen } from 'lucide-react';
import { useTranslation } from "react-i18next";

import { getBlogImageProps } from '../utils/imageUtils';
import { SITE_URL } from '../config/site';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  coverImageUrl: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  tags: string[] | null;
  categorySlug: string | null;
  categoryName: string | null;
  authorName: string;
  publishedAt: string;
}

export default function BlogPostPage() {
    const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(() => {
    if (typeof window !== 'undefined') {
      const el = document.getElementById('initial-blog-post');
      if (el && el.textContent) {
        try { return JSON.parse(el.textContent); } catch (e) {}
      }
    }
    return null;
  });
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>(() => {
    if (typeof window !== 'undefined') {
      const el = document.getElementById('initial-blog-related');
      if (el && el.textContent) {
        try { return JSON.parse(el.textContent); } catch (e) {}
      }
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window !== 'undefined') {
      return !document.getElementById('initial-blog-post');
    }
    return true;
  });
  const [notFound, setNotFound] = useState(false);

  const lastFetchedLang = useRef(i18n.language);

  useEffect(() => {
    // If we already have the post from initial hydration, don't refetch
    // unless the slug or language doesn't match what we want.
    if (slug && (!post || post.slug !== slug || lastFetchedLang.current !== i18n.language)) {
      lastFetchedLang.current = i18n.language;
      fetchPost(slug);
    }
  }, [slug, i18n.language, post]);

  const fetchPost = async (slug: string) => {
    setIsLoading(true);
    try {
      const res = await api.get<BlogPost>(`/blog/posts/${slug}`, { params: { lang: i18n.language } });
      setPost(res.data);
      if (res.data.categorySlug) {
        api.get('/blog/posts', { params: { category: res.data.categorySlug, size: 4, lang: i18n.language } })
          .then(relatedRes => {
            const others = relatedRes.data.content.filter((p: BlogPost) => p.slug !== slug).slice(0, 3);
            setRelatedPosts(others);
          })
          .catch(err => console.error('Failed to load related posts', err));
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setIsLoading(false);
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          (window as any).__PRERENDER_READY = true;
        }, 100);
      }
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(i18n.language, {
      month: 'long', day: 'numeric', year: 'numeric',
    });

  const estimateReadTime = (content?: string) => {
    if (!content) return 1;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.max(1, Math.round(words / 200));
  };

  // Simple markdown-like rendering for content
  const renderContent = (content?: string) => {
    // Content is stored as HTML — render directly
    return { __html: content || '' };
  };

  if (isLoading) {
    return (
      <BlogLayout>
        <div className="blog-post-container animate-fade-in">
          <div className="skeleton-line" style={{ width: 100, height: 16, marginBottom: 24 }} />
          <div className="skeleton-line" style={{ width: '80%', height: 36, marginBottom: 16 }} />
          <div className="skeleton-line" style={{ width: '50%', height: 16, marginBottom: 32 }} />
          <div className="skeleton-line" style={{ width: '100%', height: 300 }} />
        </div>
      </BlogLayout>
    );
  }

  if (notFound || !post) {
    return <NotFoundPage />;
  }

  const absoluteCoverImageUrl = post?.coverImageUrl
    ? (post.coverImageUrl.startsWith('http') ? post.coverImageUrl : `${SITE_URL}${post.coverImageUrl.startsWith('/') ? '' : '/'}${post.coverImageUrl}`)
    : undefined;

  const jsonLd = post ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: absoluteCoverImageUrl,
    author: { '@type': 'Organization', name: post.authorName },
    publisher: {
      '@type': 'Organization',
      name: 'Reviio',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.svg?v=2` },
    },
    datePublished: post.publishedAt,
    url: typeof window !== 'undefined' ? `${SITE_URL}${window.location.pathname}` : `${SITE_URL}/blog/${post.slug}`,
  } : undefined;

  return (
    <BlogLayout>
      {post && <script type="application/json" id="initial-blog-post" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(post).replace(/</g, '\\u003c') }} />}
      {relatedPosts.length > 0 && <script type="application/json" id="initial-blog-related" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(relatedPosts).replace(/</g, '\\u003c') }} />}
      {post && (
        <SEO 
          title={post.metaTitle || post.title}
          description={post.metaDescription || post.excerpt || ''}
          type="article"
          image={post.coverImageUrl || undefined}
          schema={jsonLd}
        />
      )}
      <article className="blog-post-container blog-article animate-fade-in">
        <Link to="/blog" className="blog-post-back">
          <ArrowLeft size={16} /><span>{` ${t('blog.post.backToBlog')}`}</span></Link>

        <header className="blog-post-header">
          {post.tags && post.tags.length > 0 && (
            <div className="blog-card-tags" style={{ marginBottom: 12 }}>
              {post.tags.map(tag => (
                <span key={tag} className="blog-card-tag">{tag}</span>
              ))}
            </div>
          )}
          <h1>{post.title}</h1>
          <div className="blog-post-meta">
            <span><User size={14} className="blog-icon-2" />{` ${post.authorName}`}</span>
            <span><Calendar size={14} className="blog-icon-2" />{` ${formatDate(post.publishedAt)}`}</span>
            <span><Clock size={14} className="blog-icon-2" />{` ${estimateReadTime(post.content)} ${t('blog.post.minRead')}`}</span>
            {post.categorySlug && post.categoryName && (
              <span>
                <Link to={`/blog/category/${post.categorySlug}`} style={{ color: 'var(--color-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                  <BookOpen size={14} className="blog-icon-2" />{` ${post.categoryName}`}
                </Link>
              </span>
            )}
          </div>
        </header>

        {post.coverImageUrl && (() => {
          const imgProps = getBlogImageProps(post.coverImageUrl);
          if (!imgProps) return null;
          return (
            <picture>
              {imgProps.srcSet && (
                <source type="image/webp" srcSet={imgProps.srcSet} sizes="(max-width: 768px) 100vw, 780px" />
              )}
              <img
                src={imgProps.src}
                alt={post.title}
                className="blog-post-cover"
                width={800}
                height={450}
                fetchPriority="high"
                loading="eager"
              />
            </picture>
          );
        })()}

        <div
          className="blog-post-content blog-content"
          suppressHydrationWarning
          dangerouslySetInnerHTML={renderContent(post.content)}
        />
        
        {relatedPosts.length > 0 && (
          <div className="blog-related-posts" style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid var(--color-border)' }}>
            <h3 style={{ marginBottom: '20px' }}><span>{`${t('blog.post.moreFrom')} `}</span><Link to={`/blog/category/${post.categorySlug}`} style={{ color: 'var(--color-primary)' }}>{post.categoryName}</Link>
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {relatedPosts.map(rp => (
                <li key={rp.id}>
                  <Link to={`/blog/${rp.slug}`} style={{ textDecoration: 'none', color: 'var(--color-text)', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                    <span>{`→ ${rp.title}`}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    </BlogLayout>
  );
}
